/**
 * 需求详情页组件
 */

import React, { useEffect, useState } from 'react';
import { Button, Spin, Tag, Card } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import demandService, { DemandItem } from '../../../services/api/demandService';
import { getCategoryLabel, getIndustryLabel, getTechnologyLabel, getTechnologyLabels } from '../../../utils/dictConverter';
import './DemandDetailPage.css';

const DemandDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [demand, setDemand] = useState<DemandItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 获取返回路径，根据来源页签决定
  const getBackPath = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    
    // 如果是从需求发布页签进入的，返回时带上 tab=demand 参数
    if (tab === 'demand') {
      return '/show/demand?tab=demand';
    }
    // 默认返回到专利池页签
    return '/show/demand?tab=patent-pool';
  };

  // 从URL路径中提取ID
  useEffect(() => {
    const loadDemandDetail = async () => {
      let id: number = 0;
      const pathname = location.pathname;
      
      // 匹配 /show/demand/123 格式
      const match = pathname.match(/\/(show\/)?demand\/(\d+)$/);
      if (match && match[2]) {
        id = parseInt(match[2]);
      }
      
      if (!id || isNaN(id)) {
        setError('无效的需求ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setDemand(null);

      try {
        const response = await demandService.getById(id);
        
        if (!response) {
          setError('需求不存在');
          setLoading(false);
          return;
        }
        
        // 检查响应格式
        if (response.code === 200 || response.code === 0) {
          // 标准响应格式: { code: 200, data: {...} }
          const data = response.data;
          if (data && (data.id || data.title)) {
            setDemand(data as DemandItem);
          } else {
            setError('需求不存在');
          }
        } else if (response.code && response.code !== 200) {
          // API返回错误
          setError(response.message || '需求不存在');
        } else {
          // 尝试将整个响应作为数据对象（兼容某些直接返回数据的接口）
          const responseAny = response as any;
          if (responseAny.id || responseAny.title) {
            setDemand(responseAny as DemandItem);
          } else {
            setError('需求不存在');
          }
        }
      } catch (apiError: any) {
        if (apiError.response) {
          if (apiError.response.status === 404) {
            setError('需求不存在');
          } else {
            setError(`加载失败: ${apiError.response.data?.message || apiError.message}`);
          }
        } else {
          setError(`加载失败: ${apiError.message || '网络错误'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadDemandDetail();
  }, [location.pathname]);

  // 获取需求类型标签颜色
  const getTypeColor = (type: string): string => {
    return type === 'supply' ? '#1890ff' : '#52c41a';
  };

  const typeColor = demand ? getTypeColor(demand.demandType) : '#1890ff';
  const typeLabel = demand ? (demand.demandType === 'supply' ? '供应' : '需求') : '';

  // 核心技术标签（支持逗号分隔的多值）
  const technologyLabels = demand?.technology
    ? getTechnologyLabels(demand.technology as any)
    : [];

  // 加载中状态
  if (loading) {
    return (
      <div className="demand-detail-page">
        <div className="demand-detail-content">
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  // 错误或数据不存在
  if (error || !demand) {
    const backPath = getBackPath();
    return (
      <div className="demand-detail-page">
        <div className="demand-detail-content">
          <Button onClick={() => navigate(backPath)} className="back-button">
            ← 返回列表
          </Button>
          <h1>需求不存在</h1>
          <p>抱歉，您访问的需求不存在。</p>
          <Button onClick={() => navigate(backPath)}>返回列表</Button>
        </div>
      </div>
    );
  }

  const backPath = getBackPath();

  return (
    <div className="demand-detail-page">
      <div className="demand-detail-content">
        {/* 返回按钮 */}
        <Button 
          onClick={() => navigate(backPath)} 
          className="back-button"
          style={{ marginBottom: '24px' }}
        >
          ← 返回列表
        </Button>

        {/* 需求信息卡片 */}
        <Card className="demand-detail-card">
          <div className="demand-detail-wrapper">
            {/* 标题和类型标签区域 */}
            <div className="demand-header-section">
              <div className="demand-title-section">
                <h1 className="demand-title">{demand.title}</h1>
                <Tag color={typeColor} className="demand-type-tag">
                  {typeLabel}
                </Tag>
              </div>
            </div>

            {/* 内容区域 */}
            <div className="demand-content-section">
              {/* 需求描述 */}
              {demand.description && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">需求描述</h3>
                  <div className="demand-content-text">
                    {demand.description}
                  </div>
                </div>
              )}
              
              {/* 需求分类 */}
              {demand.category && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">需求分类</h3>
                  <div className="demand-content-text">
                    {getCategoryLabel(demand.category) || demand.category}
                  </div>
                </div>
              )}
              
              {/* 所属行业 */}
              {demand.industry && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">所属行业</h3>
                  <div className="demand-content-text">
                    {getIndustryLabel(demand.industry) || demand.industry}
                  </div>
                </div>
              )}
              
              {/* 核心技术 */}
              {demand.technology && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">核心技术</h3>
                  <div className="demand-content-text">
                    {technologyLabels.length > 0
                      ? technologyLabels.join('、')
                      : getTechnologyLabel(demand.technology) || demand.technology}
                  </div>
                </div>
              )}
              
              {/* 企业名称 */}
              {demand.companyName && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">企业名称</h3>
                  <div className="demand-content-text">
                    {demand.companyName}
                  </div>
                </div>
              )}
              
              {/* 联系方式 */}
              {(demand.contactPerson || demand.contactPhone || demand.contactEmail) && (
                <div className="demand-content-item">
                  <h3 className="demand-content-title">联系方式</h3>
                  <div className="demand-contact-row">
                    {demand.contactPerson && (
                      <div className="demand-contact-item">
                        <span className="contact-label">联系人：</span>
                        <span className="contact-value">{demand.contactPerson}</span>
                      </div>
                    )}
                    {demand.contactPhone && (
                      <div className="demand-contact-item">
                        <span className="contact-label">联系电话：</span>
                        <a href={`tel:${demand.contactPhone}`} className="demand-link contact-value">
                          {demand.contactPhone}
                        </a>
                      </div>
                    )}
                    {demand.contactEmail && (
                      <div className="demand-contact-item">
                        <span className="contact-label">联系邮箱：</span>
                        <a href={`mailto:${demand.contactEmail}`} className="demand-link contact-value">
                          {demand.contactEmail}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="demand-actions">
              <Button 
                size="large"
                onClick={() => navigate(backPath)}
                className="action-button"
              >
                返回列表
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DemandDetailPage;

